import {
  Box,
  Button,
  Card,
  Loader,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  WixDesignSystemProvider,
  Image,
  Notification,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import React, { useEffect } from "react";
import HeroImage from "./hero.png";
import { ReactTagManager } from "react-gtm-ts";
import * as Icons from "@wix/wix-ui-icons-common";
import { useTranslation } from "react-i18next";
import "./App.css";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const SLIDE_COUNT = 3;
const INTERVAL = 3000;

function App() {
  const { t } = useTranslation();

  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [, setIsUpgraded] = React.useState(false);
  const [index, setIndex] = React.useState<number>(0);

  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  });

  const isFree = instanceData?.instance?.isFree !== false;
  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] =
    React.useState<boolean>(isFree);

  const BASE_URL = `https://wix.certifiedcode.io/clock/_functions`;

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    setInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  useEffect(() => {
    const sliderInterval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % SLIDE_COUNT);
    }, INTERVAL);

    return () => clearInterval(sliderInterval);
  }, []);

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setInstanceData(data);
        setIsUpgraded(data.instance.isFree === false);
        setIsUpgradeBannerOpen(data.instance.isFree !== false);
      })
      .catch(() => {});
  }

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${WIX_APP_ID}&redirectUrl=https://wix.certifiedcode.io/clock/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;
  const message = t("upgradeMessage", { content: t(`content.${index}`) });

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>{message}</Notification.TextLabel>
        <Notification.ActionButton
          type="textLink"
          link={upgradeUrl}
          target="_blank"
          onClick={() => {
            track("upgrade_clicked", { source: "banner" });
          }}
        >
          {t("upgrade-button")}
        </Notification.ActionButton>
        <Notification.CloseButton
          className="close-button"
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>
      <Card
        className={`${isUpgradeBannerOpen ? "App-header-notification" : ""}`}
      >
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              <MarketingPageLayoutContent
                title={t("clock-title")}
                content={t("clock-description")}
                actions={
                  <Box direction="vertical" gap="SP2">
                    <Button
                      skin="premium"
                      prefixIcon={<Icons.PremiumFilled />}
                      as="a"
                      href={upgradeUrl}
                      target="_blank"
                      onClick={() => track("upgrade_clicked", { source: "cta" })}
                    >
                      {t("clock-unlock-button")}
                    </Button>
                    <Button
                      priority="secondary"
                      prefixIcon={<Icons.GetStarted />}
                      onClick={() => {
                        track("setup_guide_clicked");
                        window.Intercom("showArticle", 9007330);
                      }}
                    >
                      {t("setup_guide")}
                    </Button>
                    <Button
                      skin="inverted"
                      prefixIcon={<Icons.Reviews />}
                      as="a"
                      href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                      target="_blank"
                      onClick={() => track("write_review_clicked")}
                    >
                      {t("clock-review-buttn")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
          image={<Image borderRadius={0} src={HeroImage} />}
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
